import { createPortal } from "react-dom";
import React, { useEffect, useState, Fragment } from "react";
import { PopUp } from "../../../Models";
import { Transition } from '@headlessui/react'
import { TagIcon } from "@heroicons/react/24/outline";
import { ArrowPathIcon } from "@heroicons/react/20/solid";
import { NIL } from "uuid";
import { AssignCompanySubtags, assignTagCompanies, assignTagUniversities, AssignUniversitySubtags, getTags } from "../../../api/tag.api";
import useApiToken from "../../../hooks/useApiToken";

interface Props {
  isOpen: boolean;
  onCancel: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  tab: string;
  refreshTags: () => void;
  setNotificationPopUp: (notificationObject: PopUp) => void;
  setShow: (show: boolean) => void;
  rankType?: string;
  selectedColumns: string[];
  getData: (obj: any) => void;
  view: string;
}

interface Subtag {
  id: string,
  name: string,
  tagSubtagId: string
}

interface Tag {
  id: string;
  name: string;
  subtags: Subtag[]
}


const AssignTagModal = (props: Props) => {
  const token = useApiToken();
  const [tags, setTags] = useState<Tag[]>([]);
  const [chosenTags, setChosenTags] = useState<string[]>([]);
  const [chosenSubtags, setChosenSubtags] = useState<string[]>([]);

  useEffect(() => {
    if (props.isOpen) {
      document.body.style.overflowY = "hidden";
    } else {
      document.body.style.overflowY = "auto";
    }
    return () => {
      document.body.style.overflowY = "auto";
    };

  }, [props.isOpen]);

  useEffect(() => {
    getTagList();
  }, [props.rankType, props.tab, token]);

  const getTagList = async () => {
    if (!token) return;
    const res = await getTags(props.view === "quarantine" ? NIL : props.rankType, props.tab === "Companies", props.view, token);
    if(res === undefined){
      return;
    }
    const tagsCombobox = res.map((tag:any) => {
                            return { id: tag.id, name: tag.name, subtags: [...tag.subtags]}
                        });

    setTags(tagsCombobox);
  }

  const onToggleTag = (tagId: string) => {
      //Its a tag
      setChosenTags((prev) => {
        return prev.some((item) => item === tagId) ?
        prev.filter((item) => item !== tagId) : 
         [...prev, tagId]
      });
  }


  const onToggleSubtag = (tagSubtagId: string) => {
    setChosenSubtags((prev) => {
      return prev.some((item) =>  item === tagSubtagId) ? 
      prev.filter((item) => item !== tagSubtagId) : 
      [...prev, tagSubtagId];
    
    });
  }

  const setNotification = (res:any, isTag: boolean) => {
    if(res && res.error){
      props.setNotificationPopUp({
        title: `${isTag ? "Tag" : "Subtag"}  assign`,
        description: res.message,
        type: "error"
      });
    }

    props.setNotificationPopUp({
      title: `${isTag ? "Tag" : "Subtag"} assign`,
      description: `${isTag ? "Tag" : "Subtag"} is successfully assigned.`,
      type: "info"
    });
    props.setShow(true);
  }

  const onAssigning = async () => {
    if (!token) return;
    if(props.tab === "Companies"){
      if(chosenTags.length >= 1){
        const res = await assignTagCompanies(chosenTags, props.selectedColumns, token);
        setNotification(res, true);
      }

      if(chosenSubtags.length >= 1){
        const res = await AssignCompanySubtags(chosenSubtags, props.selectedColumns, token);
        setNotification(res, false);
      }
    }
    else{
      if(chosenTags.length >= 1){
        const res = await assignTagUniversities(chosenTags, props.selectedColumns, token);
        setNotification(res, true);
      }

      if(chosenSubtags.length >= 1){
        const res = await AssignUniversitySubtags(chosenSubtags, props.selectedColumns, token);
        setNotification(res, false);
      }
    }


    setChosenTags([]);
    setChosenSubtags([]);
    props.getData({pageChange: false, reload: true});
    props.onCancel(true);
  }

  return createPortal(
    <Transition.Root show={props.isOpen} as={Fragment}>
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        enterTo="opacity-100 translate-y-0 sm:scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      >
        <div
          className={`${
            props.isOpen ? "transition-all" : "hidden"
          } fixed top-0 right-0 left-0 bottom-0  w-[100vw] z-20`}
        >
          <div
            className="absolute bg-gray-400 opacity-40 top-0 w-full h-screen z-10"
            onClick={() => props.onCancel(false)}
          ></div>
          <div
            className={`absolute top-0 right-0 bottom-0 left-0 max-w-[600px] max-h-max  m-auto z-50  p-4 mt-20 `}
          >
            <header className="bg-gray-100 dark:bg-gray-700 rounded-t-xl p-2 text-center text-gray-700 dark:text-gray-400  font-medium ">
              {props.title}
            </header>
            <section className="flex flex-col h-[50vh] overflow-y-scroll bg-white items-center justify-center dark:bg-darkbglight p-8 overflow-auto gap-4 py-6 ">  
            <div className="flex justify-between items-center w-full px-8">
                <h3 className="font-medium text-black dark:text-gray-300">Tag Structure</h3>
                <button onClick={getTagList}>
                  <ArrowPathIcon className="h-4 w-4 text-black dark:text-gray-300 cursor-pointer"/>
                </button>
              </div>       
              <div className="border rounded-lg flex-1 w-[90%] overflow-y-scroll pl-8">
                <div className="p-4">
                  
                  <div className="space-y-2 ">
                    {tags?.map((tag) => {
                      return (
                        <div key={tag.id} className="space-y-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                // checked={assignedTags.some((t) => t.id === tag.id)}
                                onChange={() => onToggleTag(tag.id)}
                                className="rounded"
                            />
                            <TagIcon className="h-3 w-3 text-black dark:text-gray-300" />
                            <span className="text-black dark:text-gray-300 text-sm">{tag.name}</span>
                            </label>
                            {tag.subtags.length > 0 && (
                            <div className="ml-6 space-y-2">
                                {tag.subtags.map((subtag) => (
                                <label key={subtag.id} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                    type="checkbox"
                                    // checked={assignedTags.some((t) => t.id === subtag.id)}
                                    onChange={() => onToggleSubtag(subtag.tagSubtagId)}
                                    className="rounded"
                                    />
                                    <TagIcon className="h-3 w-3 text-black dark:text-gray-300" />
                                    <span className="text-black dark:text-gray-300 text-xs">{subtag.name}</span>
                                </label>
                                ))}
                            </div>
                            )}
                        </div>
                    )
                    })}
                  </div>
                </div>
              </div>
              {/* {error.err && (
                <span className="text-[0.65rem] text-red-500 absolute left-[12rem] bottom-[6rem]">
                  *{error.message}
                </span>
              )} */}
            </section>
            <footer className="bg-gray-100 dark:bg-gray-700 rounded-b-xl p-2 text-center text-gray-700 dark:text-gray-400  font-medium">
              <div className="flex justify-end gap-3">
                <button
                  className={`inline-flex items-center rounded-md  px-3 py-2 text-sm font-semibold text-white shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
                   chosenTags.length === 0 && chosenSubtags.length === 0
                      ? "bg-gray-400 hover:bg-none"
                      : "hover:bg-indigo-700 bg-indigo-600"
                  } `}
                  onClick={onAssigning}
                  // disabled={error.err}
                >
                  Assign
                </button>
                <button
                  className="inline-flex items-center rounded-md bg-red-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={() => {
                    props.onCancel(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </footer>
          </div>
        </div>
      </Transition.Child>
    </Transition.Root>,
    document.body
  );
};

export default AssignTagModal;
                                                                                                                                               